import { Form, Formik } from "formik";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { MedicalVisit, TimePeriod } from "../types/FormTypes";
import * as Yup from "yup";
import { medicalVisitAtom } from "./AddCertificate";
import { useRef } from "react";
import { useIsAddMoreTrue } from "./AddMore";
import useFocusOnLoad from "../hooks/useFocusOnLoad";

import {
  Box,
  ErrorList,
  Button,
  handleSubmitClick,
  Progressbar,
  DatePicker,
  Heading,
} from "@ogt/ui";

export default function ByPrivate() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [formData, setFormData] = useAtom(medicalVisitAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [isAddMoreTrue] = useIsAddMoreTrue();
  const progress = isAddMoreTrue ? 87 : 62;

  return (
    <div>
      <Formik<MedicalVisit>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
          });
          navigate("../kostnader-for-overnattning");
        }}
        initialValues={{
          ...formData,
          ...{
            receipts: [],
            travelDescription: "",
          },
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          dateTo: Yup.string()
            .required("Vänligen ange datum")
            .isOccurredDate()
            .isDateNoEarlierThan(1, TimePeriod.Year),
          dateFrom: Yup.string()
            .required("Vänligen ange datum")
            .isOccurredDate()
            .isDateSameOrAfter(
              "dateTo",
              "Datum från kan inte vare tidigare än datum till"
            ),
        })}
      >
        {({ errors, touched, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-40 w-full max-w-xs mb-4">
                {isAddMoreTrue ? (
                  <Progressbar percentage={progress} />
                ) : (
                  <Progressbar percentage={progress} />
                )}
              </div>
            </div>
            <Form ref={formRef}>
              <Box>
                <>
                  <Heading progress={progress} headingRef={h2Ref} focusable>
                    Om resan
                  </Heading>

                  <ErrorList errors={errors} />

                  <Heading as="h3">När du reser med privatbil</Heading>
                  <ul className="mb-10">
                    <li>
                      Du behöver inte bifoga något kvitto eller ange sträcka. Vi
                      beräknar längden på resan från den adress som du är
                      folkbokförd på, till adressen för vårdenheten du besökt.
                      Uppgifterna hittar vi eftersom du angett personnummer och
                      från det intyg du bifogat.
                    </li>
                    <li>
                      Du betalar alltid egenavgiften, 120 kronor per enkelresa,
                      oavsett hur du reser. Det är kostnaderna utöver det som du
                      kan få ersättning för.
                    </li>
                    <li>
                      Resa med egen bild ersätts med 2,50 kronor per kilometer.
                    </li>
                  </ul>

                  <Heading as="h3" className="mb-6">
                    När reste du med bil?
                  </Heading>
                  <div className="md:flex md:w-11/12">
                    <div className="mb-6 md:mb-0 md:mr-8 md:w-1/2">
                      <DatePicker
                        name="dateTo"
                        label="Till Vårdenhet (Obligatorisk)"
                        value={formData.dateTo}
                        disableFutureDates
                      />
                    </div>
                    <div className="md:w-1/2">
                      <DatePicker
                        name="dateFrom"
                        label="Från Vårdenhet (Obligatorisk)"
                        value={formData.dateFrom}
                        disableFutureDates
                      />
                    </div>
                  </div>
                </>
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    onClick={handleSubmitClick(formRef)}
                    className="btn secondary"
                    type="submit"
                  >
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
